import React from 'react';
import ISummary from './ISummary';
import * as style from './SummaryStyle.module.scss'
import CurrencyElement from '@appComponents/CurrencyElement/CurrencyElement';
import Big from 'big.js';
import ToolTip from '@appComponents/ToolTip/ToolTip';
import InfoBox from '@appComponents/InfoBox/InfoBox';
import { MAIN_CURRENCY } from '@bundles/consts';
import SummaryList from './SummaryList';

const big = Big();

const Summary: React.FunctionComponent<ISummary> = ({
  paymentDetails,
  isSellPage,
  userWallets,
  isOrderType,
  totalValue,
  spvName,
}) => {

  const getUsdToPay = (): Big =>
    !paymentDetails.discountIsOn
      ? big(totalValue).plus(paymentDetails.fee)
      : big(totalValue);

  const getUsdBalanceTooltipMessage = () => {
    const calculateUsdFee = isSellPage
      ? big(totalValue).minus(paymentDetails.fee).toString()
      : big(totalValue).plus(paymentDetails.fee).toString();
    const feeAmount = paymentDetails.discountIsOn
      ? totalValue
      : calculateUsdFee;

    return (
      <>
        <CurrencyElement value={userWallets.usdWallet.balance} currency={MAIN_CURRENCY} />
        {isSellPage ? ' + ' : ' - '}
        <CurrencyElement value={feeAmount} currency={MAIN_CURRENCY} />
      </>
    );
  };

  const getBftBalanceTooltipMessage = () => (
    <>
      <CurrencyElement
        value={userWallets.bftWallet.balance}
        currency="bft"
      />
      &nbsp;-&nbsp;
      <CurrencyElement
        value={paymentDetails.fee}
        currency="bft"
      />
    </>
  );

  return (
    <div className={style.summary}>
      <SummaryList name="Order Value" cy="order-value">
        <CurrencyElement
          value={totalValue}
          currency={MAIN_CURRENCY}
        />
      </SummaryList>
      <SummaryList name="SPV Name" cy="spv-name">
        {spvName}
      </SummaryList>
      <SummaryList name="Payment Method" cy="payment-method">
        BF Funds
      </SummaryList>
      <SummaryList name="Trade Fee" cy="trade-fee">
        <CurrencyElement
          value={paymentDetails.fee}
          currency={paymentDetails.discountIsOn ? 'BFT' : MAIN_CURRENCY}
        />
      </SummaryList>
      <SummaryList name={isSellPage ? 'Total to pay out' : 'Total to pay'} cy="total-to-pay">
        {paymentDetails.discountIsOn ? (
          <>
            <CurrencyElement value={totalValue} currency={MAIN_CURRENCY} />
            &nbsp;{isSellPage ? '-' : '+'}&nbsp;
            <CurrencyElement value={paymentDetails.fee} currency="bft" />
          </>
        ) : (
          <CurrencyElement
            value={
              isSellPage
                ? big(totalValue).minus(paymentDetails.fee)
                : big(totalValue).plus(paymentDetails.fee)
            }
            currency={MAIN_CURRENCY}
          />
        )}
      </SummaryList>
      <SummaryList name="USD Balance After Transaction" cy="usd-balance">
        <CurrencyElement
          value={
            isSellPage
              ? paymentDetails.discountIsOn
                ? big(userWallets.usdWallet.balance).plus(totalValue)
                : big(userWallets.usdWallet.balance).plus(totalValue).minus(paymentDetails.fee)
              : big(userWallets.usdWallet.balance).minus(getUsdToPay())
          }
          bold={true}
          currency={MAIN_CURRENCY}
        />
        <ToolTip
          maxWidth={300}
          text={getUsdBalanceTooltipMessage()}
          className={style.decoration}
        >
          <span className={style.tooltipIcon}>&nbsp; &#9432;</span>
        </ToolTip>
      </SummaryList>
      {paymentDetails.discountIsOn && (
        <SummaryList name="BFT Balance After Transaction" cy="bft-balance">
          <CurrencyElement
            value={big(userWallets.bftWallet.balance).minus(
              paymentDetails.fee
            )}
            currency="bft"
            bold={true}
          />
          <ToolTip
            maxWidth={300}
            text={getBftBalanceTooltipMessage()}
            className={style.decoration}
          >
            <span className={style.tooltipIcon}>&nbsp; &#9432;</span>
          </ToolTip>
        </SummaryList>
      )}
      {!isSellPage && isOrderType && (
        <InfoBox bold={true}>
          <p data-cy="order-info">
            After placing this order{' '}
            <CurrencyElement value={getUsdToPay()} currency="USD" />
            {paymentDetails.discountIsOn && (
              <>
                &nbsp;and&nbsp;
                <CurrencyElement value={paymentDetails.fee} currency="BFT" />
              </>
            )}
            &nbsp;will be frozen on your account!
          </p>
        </InfoBox>
      )}
    </div>
  );
};

export default Summary;
